import { useEffect } from "react";

import { useWatch } from "react-hook-form";

import { useTracker } from "../../../../hooks/useTracker";
import useTranslation from "../../../../hooks/useTranslation";
import { CurrencySymbols, SubscriptionPeriod, SubscriptionTier } from "../../../../types";
import { useCheckoutFormContext } from "../CheckoutFormProvider";
import { CheckoutSummaryRow } from "../CheckoutSummaryRow/CheckoutSummaryRow";

import style from "./PriceSummary.module.css";

export const PriceSummary = () => {
  const { t, m, formatDate } = useTranslation();
  const { methods, estimateMutationResult, checkout } = useCheckoutFormContext();
  const tier = checkout?.tier;
  const isSoloTier = tier === SubscriptionTier.Individual;
  const control = methods?.control;

  const { data: estimateData, isPending: isEstimatePending } = estimateMutationResult ?? {};
  const trialEnd = estimateData?.estimate?.trialEnd ?? checkout?.trialEnd;
  const tracker = useTracker();
  useEffect(() => {
    if (estimateData?.estimate.unitAmount !== undefined) {
      const priceShownConfig: Parameters<typeof tracker.trackPriceShown>[0] = {
        currencyCode: estimateData?.estimate.currencyCode ?? "USD",
        ...(estimateData?.estimate.period === SubscriptionPeriod.Yearly
          ? { yearlyPrice: Number(estimateData?.estimate.unitAmount ?? 0) }
          : { monthlyPrice: Number(estimateData?.estimate.unitAmount ?? 0) }),
      };

      tracker.trackPriceShown(priceShownConfig);
    }
  }, [
    estimateData?.estimate.unitAmount,
    estimateData?.estimate.currencyCode,
    estimateData?.estimate.period,
    tracker,
  ]);

  const isDiscountApplied =
    estimateData?.estimate?.discountAmount !== undefined &&
    Number.parseFloat(estimateData?.estimate.discountAmount) > 0;

  const couponCode = useWatch({ name: "couponCode", control });
  const selectedPeriod = useWatch({ name: "period", control });

  const currencySymbol = (() => {
    if (estimateData?.estimate?.currencyCode) {
      return CurrencySymbols.get(estimateData?.estimate.currencyCode) ?? "$";
    }
  })();

  const unitAmount = estimateData?.estimate?.unitAmount;
  const seats = estimateData?.estimate?.editorSeatCount;

  const periodText =
    selectedPeriod === SubscriptionPeriod.Yearly
      ? t(m.Checkout.Summary.OneYear)
      : t(m.Checkout.Summary.OneMonth);
  const subTitle = isSoloTier
    ? undefined
    : t(m.Checkout.Summary.SeatPrice, {
        seats,
        symbol: currencySymbol,
        amount: unitAmount,
        period: periodText,
      });

  const planName = (() => {
    switch (tier) {
      case SubscriptionTier.Individual:
        return t(m.Checkout.Summary.Shapr3DSolo);
      case SubscriptionTier.Studio:
        return t(m.Checkout.Summary.Shapr3DStudio);
      default:
        return t(m.Checkout.Summary.Shapr3DPro);
    }
  })();

  return (
    <>
      <div className={style.checkoutLineText}>{t(m.Checkout.Summary.Title)}</div>

      <CheckoutSummaryRow
        name={planName}
        subTitle={subTitle}
        price={estimateData?.estimate.amount}
        discountedPrice={estimateData?.estimate.amountWithSubscriptionMigration}
        priceTooltipText={
          estimateData?.estimate.amountWithSubscriptionMigration &&
          t(m.Checkout.Summary.MigrationDiscount)
        }
        currencySymbol={currencySymbol}
        isLoading={isEstimatePending}
      />
      <CheckoutSummaryRow
        name={t(m.Checkout.Summary.TaxVat)}
        price={estimateData?.estimate.taxAmount}
        currencySymbol={currencySymbol}
        isLoading={isEstimatePending}
      />
      {couponCode && isDiscountApplied && (
        <CheckoutSummaryRow
          name={couponCode}
          hasDiscount
          isNameHighlighted
          isPriceHighlighted
          price={estimateData?.estimate.discountAmount}
          currencySymbol={currencySymbol}
          isLoading={isEstimatePending}
        />
      )}
      <div className={style.horizontalLine} />
      {trialEnd && (
        <>
          <CheckoutSummaryRow
            name={t(m.Checkout.Summary.DueOn, { date: formatDate(trialEnd) })}
            price={estimateData?.estimate.totalAmount}
            currencySymbol={currencySymbol}
            isLoading={isEstimatePending}
          />
          <div className={style.horizontalLine} />
        </>
      )}
      <CheckoutSummaryRow
        name={t(m.Checkout.Summary.TotalDueToday)}
        price={trialEnd ? "0" : estimateData?.estimate.totalAmount}
        currencySymbol={currencySymbol}
        isLoading={isEstimatePending}
        isPriceHighlighted
      />
    </>
  );
};
